import { Chart, ChartArea, ChartType, TooltipModel } from 'chart.js'

import { formatValue } from 'components_new/organisms/Widget/Types/utils'

import { getTheme } from 'themes'

import { getDataTooltipSurface } from 'components_new/atoms/Tooltip/dataSurface'

import { TChartDataset, TChartVariant } from '../types'
import { getHoveredMarkers } from '../plugins/drawMarkers'

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const getOrCreateTooltipEl = (chart: Chart) => {
  let el = chart.canvas.parentNode?.querySelector<HTMLDivElement>(
    '.chartjs-ext-tooltip'
  )

  if (!el) {
    el = document.createElement('div')
    el.className = 'chartjs-ext-tooltip'
    ;(chart.canvas.parentNode as HTMLElement).style.position ||= 'relative'

    // position
    el.style.position = 'absolute'
    el.style.left = '0'
    el.style.top = '0'
    el.style.pointerEvents = 'none'
    el.style.opacity = '0'

    // transition
    el.style.transition = 'opacity 0.08s ease, transform 0.08s ease'
    el.style.willChange = 'transform'

    // shape, surface and text — shared with the React data tooltips
    Object.assign(el.style, getDataTooltipSurface())

    chart.canvas.parentNode?.appendChild(el)
  }

  return el
}

const TOOLTIP_OFFSET = 24

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max))

// Tooltip placement: to the right of the cursor, flipped to the left when it
// would overflow the right edge, then clamped inside the chart area.
const getTooltipPosition = (params: {
  chartArea: ChartArea
  caretX: number
  caretY: number
  width: number
  height: number
}) => {
  const { chartArea, caretX, caretY, width, height } = params
  const { left, right, top, bottom } = chartArea

  const fitsToTheRight = caretX + TOOLTIP_OFFSET + width <= right

  const x = fitsToTheRight
    ? caretX + TOOLTIP_OFFSET
    : caretX - TOOLTIP_OFFSET - width
  const y = caretY - height / 2

  return {
    x: clamp(x, left, Math.max(left, right - width)),
    y: clamp(y, top, Math.max(top, bottom - height))
  }
}

const getRowHtml = (params: {
  color: string
  label: string
  theme: ReturnType<typeof getTheme>
  type: ChartType
  unit?: string | null
  value: string
}) => {
  const { color, label, theme, type, unit, value } = params

  return `
            <tr>
              ${
  type === 'bubble'
    ? ''
    : `<td style="
                border: none;
                padding: 2px 6px;
                text-align: left;
                white-space: nowrap;
                ">
                    <div style="
                    border-radius: 4px;
                    height: 8px;
                    width: 8px;
                    background-color: ${color}
                    "/>
                </td>`
}
                <td style="
                border: none;
                color: ${theme.palette.text.secondary};
                padding: ${type === 'bubble' ? '2px 6px 2px 0px' : '2px 6px'};
                text-align: left;
                white-space: nowrap;
                ">
                ${escapeHtml(label)}
                </td>

                <td style="
                border: none;
                padding: 2px 6px;
                text-align: right;
                font-variant-numeric: tabular-nums;
                white-space: nowrap;
                font-weight: ${theme.typography.fontWeightBold};
                ">
                ${value}
                </td>

                ${
  unit
    ? `<td style="
                border: none;
    color: ${theme.palette.text.secondary};
    padding: 2px;
    text-align: left;
    white-space: nowrap;
    ">
    ${unit}
    </td>`
    : ''
}
            </tr>
        `
}

export const externalTooltipHandler = (params: {
  ctx: {
    chart: Chart
    tooltip: TooltipModel<ChartType>
  }
  unit: TChartDataset['unit']
  variant: TChartVariant
  decimals?: number
  /** A chart in a table cell: shown in every variant, beside the chart. */
  compact?: boolean
  getRows?: (dataPoints: TooltipModel<ChartType>['dataPoints']) => {
    color: string
    label: string
    type: ChartType
    unit?: string | null
    value: string
  }[]
  significantFigures?: number

  /** Overrides the shared surface, e.g. opaque and above a table. */
  style?: Partial<CSSStyleDeclaration>
}) => {
  const {
    compact,
    ctx,
    unit,
    variant,
    decimals,
    getRows,
    significantFigures,
    style
  } = params
  const { chart, tooltip } = ctx

  if (variant !== 'detailed' && !compact) return

  const el = getOrCreateTooltipEl(chart)

  if (tooltip.opacity === 0) {
    el.style.opacity = '0'

    return
  }

  const theme = getTheme('light')

  const title = `
              <span style="
                color: ${theme.palette.text.primary};
                font-weight: ${theme.typography.fontWeightBold};
                white-space: nowrap;
                ">${tooltip.title}</span>
  `

  // a hovered pin lists its markers instead of the data
  const markerRows = getHoveredMarkers(chart)
    .filter((marker) => marker.label)
    .map((marker) =>
      getRowHtml({
        color: marker.backgroundColor ?? theme.palette.text.secondary,
        label: marker.label ?? '',
        theme,
        type: 'line',
        value: ''
      })
    )
    .join('')

  const dataRows = getRows
    ? getRows(tooltip.dataPoints)
      .map((row) => getRowHtml({ ...row, theme, unit: row.unit ?? unit }))
      .join('')
    : tooltip.dataPoints
      ?.map((dp) => {
        let bg = dp.element?.options?.backgroundColor as any

        if (Array.isArray(bg)) {
          bg = bg[dp.dataIndex]
        }

        if (typeof bg !== 'string') {
          bg = 'rgba(0,0,0,0.12)'
        }

        const color = bg
        const label = dp.dataset.label ?? ''
        const value = String(
          formatValue(
              dp.raw as number,
              null,
              decimals ?? 1,
              undefined,
              undefined,
              significantFigures
          )
        )

        return getRowHtml({
          color,
          label,
          theme,
          type: (dp.dataset.type ?? 'line') as ChartType,
          unit,
          value
        })
      })
      .join('') ?? ''

  const rows = markerRows || dataRows

  el.innerHTML = `<div>
  ${title}
  <table style="
    border-collapse: collapse;
    width: 100%;
  ">
    <tbody>${rows}</tbody>
  </table>
  </div>
  `

  const { x, y } = compact
    ? {
        // The chart is smaller than the tooltip: beside the cursor, centred
        // on the chart, not kept inside it.
        x: tooltip.caretX + TOOLTIP_OFFSET,
        y: (chart.height - el.offsetHeight) / 2
      }
    : getTooltipPosition({
      chartArea: chart.chartArea,
      caretX: tooltip.caretX,
      caretY: tooltip.caretY,
      width: el.offsetWidth,
      height: el.offsetHeight
    })

  Object.assign(el.style, style)
  el.style.opacity = '1'
  el.style.transform = `translate3d(${x}px, ${y}px, 0)`
}
